import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import API from '../api/baseUrl'
import pathUrl from '../api/pathURL'
import {
  ShiftCommentFormType,
  ShiftPayload,
  ShiftPreferencePayload,
  ShiftPreferenceType,
} from '../types/interface'
import { getAuthToken } from './auth-token'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)

const joinShift = async (payload: ShiftPayload) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.post(pathUrl.joinShift, payload, options) // Join shift
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    throw error
  }
}

const cancelShift = async (payload: ShiftPayload) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.post(pathUrl.cancelShift, payload, options) // Cancel shift endpoint
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    return error
  }
}

const updateShift = async (payload: ShiftCommentFormType, shift_id: number) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.put(
      `${pathUrl.updateShift}${shift_id}`,
      payload,
      options
    ) // Update shift endpoint
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    return error
  }
}

const getAvailableShifts = async () => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.get(pathUrl.getAvailableShifts, options) // Check available shift endpoint
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    return error
  }
}

type Shift = {
  duration: number
  end_time: string
  start_time: string
}

const overlapShift = (
  availableShift: Shift,
  pendingShifts: Shift[],
  acceptedShifts: Shift[]
) => {
  // Check if an available shift overlaps with any pending or available shift
  const shiftOverlap = (shiftToCheck: Shift, shiftToCompare: Shift) => {
    // Compares if two shifts overlap
    const checkStartDate = dayjs(shiftToCheck?.start_time)
    const checkEndDate = dayjs(shiftToCheck?.end_time)

    const compareStartDate = dayjs(shiftToCompare.start_time)
    const compareEndDate = dayjs(shiftToCompare.end_time)

    // Compare if Available shift starts before Compared Shift ends and if ends after Compared Shift starts
    return (
      checkStartDate.isBetween(compareStartDate, compareEndDate) ||
      checkEndDate.isBetween(compareStartDate, compareEndDate)
    )
  }

  const hasPendingOverlap = pendingShifts.some((pendingShift) =>
    shiftOverlap(availableShift, pendingShift)
  )

  const hasAcceptedOverlap = acceptedShifts.some((acceptedShift) =>
    shiftOverlap(availableShift, acceptedShift)
  )

  return hasPendingOverlap || hasAcceptedOverlap
}

const overworkedShift = (pendingShifts: Shift[], acceptedShifts: Shift[]) => {
  // Check if total duration is over 12h
  const pendingDuration = pendingShifts.reduce(
    (total, shift) => total + shift.duration,
    0
  )
  const acceptedDuration = acceptedShifts.reduce(
    (total, shift) => total + shift.duration,
    0
  )
  const totalDuration = pendingDuration + acceptedDuration

  return totalDuration > 12
}

const createPreference = async (payload: ShiftPreferencePayload[]) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const body = {
      data: [...payload],
    }
    const response = await API.post(pathUrl.shiftPreference, body, options) // set shift preference
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    throw error
  }
}

const deletePreference = async (id: number) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.delete(
      `${pathUrl.shiftPreference}/${id}`,
      options
    ) // delete shift preference
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    throw error
  }
}

const updatePreference = async (id: number, payload: ShiftPreferenceType) => {
  try {
    const token = await getAuthToken()
    const options = {
      // Set options
      headers: {
        Authorization: token,
        ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
      },
    }
    const response = await API.patch(
      `${pathUrl.shiftPreference}/${id}`,
      payload,
      options
    ) // update shift preference
    const data = await response.data
    return data
  } catch (error) {
    console.log(error)
    throw error
  }
}

export {
  cancelShift,
  createPreference,
  deletePreference,
  getAvailableShifts,
  joinShift,
  overlapShift,
  overworkedShift,
  updatePreference,
  updateShift,
}
